<template>
  <a-drawer
    :visible="visible"
    :width="960"
    :title="null"
    placement="right"
    @close="handleClose"
    class="alimtalk-detail-drawer"
  >
    <!-- 커스텀 헤더 -->
    <div class="detail-header">
      <div class="header-content">
        <h2>
          알림톡 발송 상세
          <span class="send-time">{{ formatDate(detail?.createdAt) }}</span>
        </h2>
        <a-tag :color="getStatusColor(detail?.status)" v-if="detail">
          {{ getStatusText(detail?.status) }}
        </a-tag>
      </div>
    </div>

    <!-- 상세 내용 -->
    <div v-if="detail" class="detail-content">
      <!-- 기본 정보 섹션 -->
      <div class="info-section">
        <h4 class="section-title">기본 정보</h4>
        <a-descriptions :column="1" bordered size="small">
          <a-descriptions-item label="메시지 전송유형">
            {{ detail.eventType || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="수신번호">
            {{ formatMobile(detail.mobile) }}
          </a-descriptions-item>
          <a-descriptions-item label="템플릿 코드">
            <a-typography-text 
              :copyable="{ tooltips: ['복사', '복사됨'] }"
              :style="{ marginBottom: 0 }"
            >
              {{ detail.templateCode || '-' }}
            </a-typography-text>
          </a-descriptions-item>
          <a-descriptions-item label="처리시간">
            <span v-if="detail.processingTime">{{ detail.processingTime }} ms</span>
            <span v-else>-</span>
          </a-descriptions-item>
        </a-descriptions>
      </div>

      <!-- 메시지 ID 섹션 -->
      <div class="info-section">
        <h4 class="section-title">메시지 ID</h4>
        <a-descriptions :column="1" bordered size="small">
          <a-descriptions-item label="UID">
            <a-typography-paragraph 
              :copyable="{ tooltips: ['복사', '복사됨'] }" 
              :style="{ marginBottom: 0 }"
              class="monospace"
            >
              {{ detail.uid || '-' }}
            </a-typography-paragraph>
          </a-descriptions-item>
          <a-descriptions-item label="CID">
            <a-typography-paragraph 
              :copyable="{ tooltips: ['복사', '복사됨'] }" 
              :style="{ marginBottom: 0 }"
              class="monospace"
            >
              {{ detail.cid || '-' }}
            </a-typography-paragraph>
          </a-descriptions-item>
        </a-descriptions>
      </div>

      <!-- 메시지 내용 섹션 -->
      <div class="info-section">
        <h4 class="section-title">메시지 내용</h4>
        <div class="message-content">
          <pre>{{ getMessageContent() }}</pre>
        </div>
      </div>

      <!-- 버튼 정보 섹션 (있는 경우만) -->
      <div class="info-section" v-if="detail.requestBody?.button && detail.requestBody.button.length > 0">
        <h4 class="section-title">버튼 정보</h4>
        <a-table
          :columns="buttonColumns"
          :data-source="buttonData"
          :pagination="false"
          size="small"
          bordered
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'url'">
              <a-typography-text 
                :copyable="{ tooltips: ['복사', '복사됨'], text: record.url }" 
                :ellipsis="true"
                style="max-width: 300px"
              >
                {{ record.url }}
              </a-typography-text>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>
        </a-table>
      </div>

      <!-- 아이템 정보 섹션 (있는 경우만) -->
      <div class="info-section" v-if="detail.requestBody?.item?.list && detail.requestBody.item.list.length > 0">
        <h4 class="section-title">아이템 정보</h4>
        <a-table
          :columns="itemColumns"
          :data-source="itemData"
          :pagination="false"
          size="small"
          bordered
        />
      </div>

      <!-- 변수 정보 섹션 (있는 경우만) -->
      <div class="info-section" v-if="detail.variables && Object.keys(detail.variables).length > 0">
        <h4 class="section-title">변수 정보</h4>
        <a-table
          :columns="variableColumns"
          :data-source="variableData"
          :pagination="false"
          size="small"
          bordered
        />
      </div>

      <!-- 오류 정보 섹션 (있는 경우만) -->
      <div class="info-section" v-if="detail.errorMessage">
        <h4 class="section-title error-title">
          <i class="fa fa-exclamation-triangle"></i> 오류 정보
        </h4>
        <a-alert
          type="error"
          :message="detail.errorMessage"
          show-icon
        />
      </div>

      <!-- 응답 정보 섹션 (있는 경우만) -->
      <div class="info-section" v-if="detail.response">
        <h4 class="section-title">응답 정보</h4>
        <div class="response-content">
          <pre>{{ formatResponse(detail.response) }}</pre>
        </div>
      </div>
    </div>
    
    <!-- 로딩 상태 -->
    <div v-else-if="loading" class="loading-container">
      <a-spin tip="로딩 중..." />
    </div>

    <!-- 데이터 없음 -->
    <div v-else class="empty-container">
      <a-empty description="데이터를 불러올 수 없습니다." />
    </div>
  </a-drawer>
</template>

<script>
import { computed } from 'vue';
import dayjs from 'dayjs';
import { STATUS_COLOR_MAP, STATUS_TEXT_MAP } from '../alimtalkApi';
import { Typography } from 'ant-design-vue';

export default {
  name: 'AlimtalkDetailDrawer',
  components: {
    'a-typography-text': Typography.Text,
    'a-typography-paragraph': Typography.Paragraph
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    detail: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const variableColumns = [
      {
        title: '변수명',
        dataIndex: 'key',
        key: 'key',
        width: 150
      },
      {
        title: '값',
        dataIndex: 'value',
        key: 'value'
      }
    ];

    const buttonColumns = [
      {
        title: '버튼명',
        dataIndex: 'name',
        key: 'name',
        width: 200
      },
      {
        title: '타입',
        dataIndex: 'type',
        key: 'type',
        width: 100
      },
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
        ellipsis: true
      }
    ];

    const itemColumns = [
      {
        title: '제목',
        dataIndex: 'title',
        key: 'title',
        width: 150
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description'
      }
    ];

    const variableData = computed(() => {
      if (!props.detail?.variables) return [];
      return Object.entries(props.detail.variables).map(([key, value]) => ({
        key,
        value: value || '-'
      }));
    });

    const buttonData = computed(() => {
      if (!props.detail?.requestBody?.button) return [];
      return props.detail.requestBody.button.map((btn, index) => ({
        key: index,
        name: btn.name || '-',
        type: btn.type || '-',
        url: btn.url_mobile || btn.url_pc || '-'
      }));
    });

    const itemData = computed(() => {
      if (!props.detail?.requestBody?.item?.list) return [];
      return props.detail.requestBody.item.list.map((item, index) => ({
        key: index,
        title: item.title || '-',
        description: item.description || '-'
      }));
    });

    const getMessageContent = () => {
      if (props.detail?.requestBody?.message) {
        return props.detail.requestBody.message;
      }
      if (props.detail?.message) {
        return props.detail.message;
      }
      return '메시지 내용이 없습니다.';
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    };

    const formatMobile = (mobile) => {
      if (!mobile) return '-';
      const cleaned = mobile.replace(/[^0-9]/g, '');
      if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
      }
      return mobile;
    };

    const getStatusColor = (status) => {
      return STATUS_COLOR_MAP[status] || '#d9d9d9';
    };

    const getStatusText = (status) => {
      return STATUS_TEXT_MAP[status] || status;
    };

    const formatResponse = (response) => {
      if (!response) return '-';
      if (typeof response === 'string') return response;
      return JSON.stringify(response, null, 2);
    };

    const handleClose = () => {
      emit('close');
    };

    return {
      variableColumns,
      buttonColumns,
      itemColumns,
      variableData,
      buttonData,
      itemData,
      getMessageContent,
      formatDate,
      formatMobile,
      getStatusColor,
      getStatusText,
      formatResponse,
      handleClose
    };
  }
};
</script>

<style scoped>
.alimtalk-detail-drawer :deep(.ant-drawer-body) {
  padding: 0;
}

.detail-header {
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.send-time {
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-left: 10px;
}

.detail-content {
  padding: 20px;
}

.info-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.section-title.error-title {
  color: #f5222d;
}

.message-content,
.response-content {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.message-content pre,
.response-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

.monospace {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  color: #666;
}

.loading-container,
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

/* 설명 항목 스타일 */
:deep(.ant-descriptions-item-label) {
  font-weight: 500;
  color: #666;
}

:deep(.ant-descriptions-item-content) {
  color: #333;
}

/* 테이블 스타일 */
:deep(.ant-table-small) {
  font-size: 13px;
}

:deep(.ant-alert) {
  font-size: 13px;
}
</style>